import React, {useState, useEffect} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import axios from 'axios'
import {toast} from 'react-toastify'
import SearchableSelect from '../../../../reusable-components/forms/SearchableSelect'

interface DownloadDialogProps {
  open: boolean
  onClose: () => void
  companyLogo?: string
  logoUrl?: string
}

const DownloadDialog: React.FC<DownloadDialogProps> = ({
  open,
  onClose,
  companyLogo,
  logoUrl,
}) => {
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null)
  const [selectedClient, setSelectedClient] = useState<{value: string | number; label: string} | null>(null)
  const [clients, setClients] = useState<{value: string; label: string}[]>([])
  const [loading, setLoading] = useState(false)
  const [clientsLoading, setClientsLoading] = useState(false)

  useEffect(() => { 
    if (open) {
      fetchClients()
    }
  }, [open])

  const fetchClients = async () => {
    try {
      setClientsLoading(true)
      const response = await axios.get('/behavior-assessment/getBehaviorAssessmentClientsForPDF')
      const {data} = response?.data
      const updatedClients = data.map((client: any) => ({
        value: client._id,
        label: client.fullName,
      }))
      setClients(updatedClients)
    } catch (error) {
      console.error('Error fetching clients:', error)
      toast.error('Failed to fetch clients')
    } finally {
      setClientsLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!startDate || !endDate || !selectedClient) {
      toast.error('Please fill in all required fields')
      return
    }

    if (startDate.isAfter(endDate)) {
      toast.error('Start date cannot be after end date')
      return
    }

    try {
      setLoading(true)

      let finalCompanyLogoUrl = ''
      if (companyLogo?.startsWith('http')) {
        finalCompanyLogoUrl = companyLogo
      } else if (logoUrl) {
        finalCompanyLogoUrl = logoUrl
      }

      const formData = {
        companyLogoUrl: finalCompanyLogoUrl,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        clientId: selectedClient.value.toString(),
        clientName: selectedClient.label,
      }

      const response = await axios.post('/behavior-assessment/generate-pdf', formData, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], {type: 'application/pdf'})
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `Behavior_Assessment_Report_${selectedClient.label}_${startDate.format('MM-DD-YYYY')}_to_${endDate.format('MM-DD-YYYY')}.pdf`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Report downloaded successfully')
      onClose()
    } catch (error) {
      console.error('Error downloading report:', error)
      toast.error('Failed to download report')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setStartDate(null)
    setEndDate(null)
    setSelectedClient(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        },
        '& .MuiDialog-paper': {
          overflow: 'visible'  // This makes the dialog content overflow visible
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          fontWeight: 600,
          fontSize: '1.25rem',
          padding: '20px 24px',
        }}
      >
        Download Behavior Assessment Report
      </DialogTitle>

      <DialogContent sx={{padding: '24px', overflow: 'visible'}}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
          <Typography variant='body1' color='text.secondary' sx={{mt: 1}}>
            Please select the criteria for generating the behavior assessment report.
          </Typography>
          <Box>
            <SearchableSelect
              label='Select Client'
              options={clients}
              value={selectedClient?.value || ''}
              onChange={(newValue) => setSelectedClient(newValue)}
              fullWidth
              size='small'
              disabled={clientsLoading}
              endAdornment={clientsLoading ? <CircularProgress size={20} /> : null}
            />
          </Box>
          {/* Date Range */}
          <Box>
            <Box sx={{display: 'flex', gap: 2}}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label='Start Date'
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  maxDate={dayjs()}
                  slotProps={{
                    field:{
                      readOnly: true,
                    },
                    textField: {
                      size: 'small',
                      disabled: false,
                      style: { width: '100%' },
                      InputProps: {
                        readOnly: true,
                        style: { cursor: 'pointer' },
                      },
                      onClick: (e) => {
                        // Prevent the click from bubbling up and trigger the date picker
                        e.preventDefault();
                        e.stopPropagation();
                        // Find and click the calendar icon to open the picker
                        const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                         e.currentTarget.querySelector('.MuiIconButton-root');
                        if (iconButton) {
                          (iconButton as HTMLElement).click();
                        }
                      }
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      WebkitTextFillColor: '#000000',
                      cursor: 'pointer',
                    },
                    '& .MuiOutlinedInput-root': {
                      cursor: 'pointer',
                    },
                    '& .MuiIconButton-root': {
                      cursor: 'pointer',
                    },
                    '& .MuiClock-pin': {
                      backgroundColor: '#1976d2',
                    },
                    '& .MuiClockPointer-root': {
                      backgroundColor: '#1976d2',
                      '& .MuiClockPointer-thumb': {
                        backgroundColor: '#1976d2',
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiPaper-root': {
                      boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      marginTop: '4px',
                    },
                    '& .MuiClock-root': {
                      pointerEvents: 'auto',
                    },
                    '& .MuiClockNumber-root': {
                      pointerEvents: 'auto',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiPickersPopper-paper': {
                        position: 'fixed !important',
                        top: 'auto !important',
                        left: '50% !important',
                        transform: 'translateX(-50%) !important',
                        width: 'calc(100% - 32px) !important',
                        maxWidth: '360px !important',
                        margin: '0 auto !important',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label='End Date'
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  maxDate={dayjs()}
                  minDate={startDate}
                  slotProps={{
                    field:{
                      readOnly: true,
                    },
                    textField: {
                      size: 'small',
                      required: true,
                      disabled: false,
                      style: { width: '100%' },
                      InputProps: {
                        readOnly: true,
                        style: { cursor: 'pointer' },
                      },
                      onClick: (e) => {
                        // Prevent the click from bubbling up and trigger the date picker
                        e.preventDefault();
                        e.stopPropagation();
                        // Find and click the calendar icon to open the picker
                        const iconButton = e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') || 
                                         e.currentTarget.querySelector('.MuiIconButton-root');
                        if (iconButton) {
                          (iconButton as HTMLElement).click();
                        }
                      }
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      WebkitTextFillColor: '#000000',
                      cursor: 'pointer',
                    },
                    '& .MuiOutlinedInput-root': {
                      cursor: 'pointer',
                    },
                    '& .MuiIconButton-root': {
                      cursor: 'pointer',
                    },
                    '& .MuiClock-pin': {
                      backgroundColor: '#1976d2',
                    },
                    '& .MuiClockPointer-root': {
                      backgroundColor: '#1976d2',
                      '& .MuiClockPointer-thumb': {
                        backgroundColor: '#1976d2',
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiPaper-root': {
                      boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      marginTop: '4px',
                    },
                    '& .MuiClock-root': {
                      pointerEvents: 'auto',
                    },
                    '& .MuiClockNumber-root': {
                      pointerEvents: 'auto',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiPickersPopper-paper': {
                        position: 'fixed !important',
                        top: 'auto !important',
                        left: '50% !important',
                        transform: 'translateX(-50%) !important',
                        width: 'calc(100% - 32px) !important',
                        maxWidth: '360px !important',
                        margin: '0 auto !important',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '16px 24px 24px',
          gap: '12px',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          sx={{
            borderColor: '#6c757d',
            color: '#6c757d',
            '&:hover': {
              borderColor: '#495057',
              backgroundColor: '#f8f9fa',
            },
            fontWeight: 500,
            padding: '8px 20px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant='contained'
          disabled={loading || !startDate || !endDate || !selectedClient}
          sx={{
            backgroundColor: '#007bff',
            '&:hover': {
              backgroundColor: '#0056b3',
            },
            fontWeight: 500,
            padding: '8px 20px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          }}
        >
          {loading ? <CircularProgress size={20} color='inherit' /> : 'Download Report'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadDialog
